/**
 * PC 端直传阿里云 OSS（立方镜 2026-09-17，与 App 端 common/ossUpload.js 同一套接口）
 *
 * 文件字节全部浏览器直连 OSS，服务端只签名：
 *   - 图片/文件：/oss/policy 拿 PostObject 签名 → 一次 POST
 *   - 视频：/oss/sts 拿 STS 临时凭证 → 分片并发上传 + 断点续传（ossMultipart.js），上限 200MB；
 *           本台没配 STS 时退回 /oss/policy 单次直传
 * 返回结构与原 /image/upload、/video/upload、/file/upload 接口的 data 一致，调用方不用改。
 *
 * 任何一步不行都返回 null，由 FileUpload 退回原来的服务器上传：
 *   - 本台没开 OSS / 开了图片内容检测 → enable=false
 *   - 本台还是旧 jar（没有这两个接口，返回 code=500）
 *   - 网络失败 / OSS 返回非 200
 * ⚠️ 故意不用 httpRequest.js 的 http：它对 code!=200 会弹错误提示，旧 jar 的台会先弹「系统繁忙」再降级。
 */
import axios from 'axios'
import { multipartUpload, fileFingerprint } from './ossMultipart.js'
import { apiBase } from './serverUrl.js'

// 服务器中转上传的大小上限，与服务端 Constant.MAX_VIDEO_SIZE / MAX_FILE_SIZE 一致
const SERVER_LIMITS = {
	video: 50 * 1024 * 1024,
	file: 20 * 1024 * 1024
}

const TYPES = {
	'/image/upload': 'image',
	'/video/upload': 'video',
	'/file/upload': 'file'
}

// 调本台签名接口（/oss/policy 或 /oss/sts），拿不到有效结果返回 null
const callSignApi = async (path, params) => {
	const accessToken = sessionStorage.getItem("accessToken");
	if (!accessToken) {
		return null;
	}
	const res = await axios({
		baseURL: apiBase(),
		url: path,
		method: 'post',
		params: params,
		headers: { accessToken: encodeURIComponent(accessToken) },
		timeout: 10000
	});
	const body = res.data;
	return body && body.code == 200 && body.data && body.data.enable ? body.data : null;
}

const getPolicy = (params) => callSignApi('/oss/policy', params);

const postToOss = (policy, file) => {
	const formData = new FormData();
	formData.append('key', policy.key);
	formData.append('policy', policy.policy);
	formData.append('OSSAccessKeyId', policy.accessKeyId);
	formData.append('signature', policy.signature);
	formData.append('success_action_status', '200');
	// file 必须放最后，OSS 会忽略 file 之后的表单域
	formData.append('file', file);
	return axios.post(policy.host, formData, { timeout: 1000 * 600 });
}

const getImageSize = (file) => {
	return new Promise((resolve) => {
		const url = URL.createObjectURL(file);
		const img = new Image();
		img.onload = () => {
			resolve({ width: img.naturalWidth, height: img.naturalHeight });
			URL.revokeObjectURL(url);
		};
		img.onerror = () => {
			resolve({ width: 0, height: 0 });
			URL.revokeObjectURL(url);
		};
		img.src = url;
	})
}

const getVideoSize = (file) => {
	return new Promise((resolve) => {
		const url = URL.createObjectURL(file);
		const video = document.createElement('video');
		video.preload = 'metadata';
		video.onloadedmetadata = () => {
			resolve({ width: video.videoWidth, height: video.videoHeight });
			URL.revokeObjectURL(url);
		};
		video.onerror = () => {
			resolve({ width: 0, height: 0 });
			URL.revokeObjectURL(url);
		};
		video.src = url;
	})
}

// ---------------- 视频：分片直传 + 断点续传 ----------------

const CHECKPOINT_PREFIX = 'oss_mp_';
const CHECKPOINT_TTL = 3 * 24 * 3600 * 1000;

const storageGet = (key) => {
	try {
		return JSON.parse(localStorage.getItem(key));
	} catch (e) {
		return null;
	}
}

const storageSet = (key, value) => {
	try {
		localStorage.setItem(key, JSON.stringify(value));
	} catch (e) {
		// 存不了就只是不能跨刷新续传，不影响本次上传
	}
}

const storageRemove = (key) => {
	try {
		localStorage.removeItem(key);
	} catch (e) {
		// 忽略
	}
}

// 清掉超过 3 天的续传记录（OSS 上没合并的分片由 bucket 生命周期规则清理）
const pruneCheckpoints = () => {
	try {
		const now = Date.now();
		Object.keys(localStorage).filter(k => k.indexOf(CHECKPOINT_PREFIX) === 0).forEach(k => {
			const cp = storageGet(k);
			if (!cp || !cp.time || now - cp.time > CHECKPOINT_TTL) {
				storageRemove(k);
			}
		});
	} catch (e) {
		// 清理失败不影响上传
	}
}

const axiosRequest = async ({ method, url, headers, body }) => {
	const res = await axios({
		method,
		url,
		headers,
		data: body,
		timeout: 1000 * 120,
		responseType: 'text',
		transformResponse: [(data) => data],
		validateStatus: () => true
	});
	return { status: res.status, data: typeof res.data === 'string' ? res.data : '' };
}

const multipartVideo = async (file) => {
	const readChunk = (start, end) => file.slice(start, end).arrayBuffer();
	const storageKey = CHECKPOINT_PREFIX + await fileFingerprint(file.size, readChunk);
	pruneCheckpoints();
	const sts = await multipartUpload({
		size: file.size,
		adapter: {
			getSts: (key) => callSignApi('/oss/sts', key ? { key } : { fileName: file.name, fileSize: file.size }),
			request: axiosRequest,
			readChunk,
			loadCheckpoint: () => storageGet(storageKey),
			saveCheckpoint: (cp) => storageSet(storageKey, Object.assign({ time: Date.now() }, cp)),
			clearCheckpoint: () => storageRemove(storageKey)
		}
	});
	return sts ? { url: sts.url, coverUrl: sts.coverUrl } : null;
}

/**
 * @param action FileUpload 的 action，如 '/image/upload?thumbSize=20'
 * @param file 浏览器 File
 * @returns 与原上传接口相同结构的 data；不能直传返回 null
 */
const ossUpload = async (action, file, isPermanent) => {
	try {
		const [path, query] = action.split('?');
		const type = TYPES[path];
		if (!type) {
			return null;
		}
		const params = { type, fileName: file.name, fileSize: file.size, isPermanent: !!isPermanent };
		const thumbSize = new URLSearchParams(query || '').get('thumbSize');
		if (thumbSize) {
			params.thumbSize = thumbSize;
		}
		if (type == 'video') {
			// 优先分片直传（断点续传）；本台没配 STS 时返回 null，继续走下面的单次直传
			const mp = await multipartVideo(file);
			if (mp) {
				const size = await getVideoSize(file);
				return { videoUrl: mp.url, coverUrl: mp.coverUrl, width: size.width, height: size.height };
			}
		}
		const policy = await getPolicy(params);
		if (!policy) {
			return null;
		}
		if (type == 'image') {
			const size = await getImageSize(file);
			await postToOss(policy, file);
			return { originUrl: policy.url, thumbUrl: policy.thumbUrl, width: size.width, height: size.height };
		}
		if (type == 'video') {
			const size = await getVideoSize(file);
			await postToOss(policy, file);
			return { videoUrl: policy.url, coverUrl: policy.coverUrl, width: size.width, height: size.height };
		}
		await postToOss(policy, file);
		return policy.url;
	} catch (e) {
		// 超过服务器中转上限的，退回服务器上传也必然被拒（白传一遍），直接抛错由 FileUpload 标记失败；
		// 视频续传记录保留，重新发送同一个文件会接着传
		const limit = SERVER_LIMITS[TYPES[action.split('?')[0]]];
		if (limit && file.size > limit) {
			console.log('OSS直传失败，文件超过服务器中转上限，不再退回服务器上传', e);
			throw e;
		}
		console.log('OSS直传失败，改走服务器上传', e);
		return null;
	}
}

export default ossUpload;
